import React, { useId, createRef } from 'react'
import { ComponentCSSProp, styled } from '@grupoboticario/flora-react-system'
import { ColorThemeProps } from '@grupoboticario/flora'
export type IconProps = {
  /**
   * Tamanho do ícone, largura e altura iguais
   * @default 24
   * @example 16 | '16px' | '1em' | '1rem'
   */
  size?: number | string
  /**
   * Cor do ícone, aceita valores de tema de cor como tokens
   * @default 'currentColor'
   * @example '$statusErrorNonPrimaryButtonActive'
   */
  color?: ColorThemeProps | 'currentColor' | 'transparent'
  children?:
    | React.ReactElement<React.SVGProps<SVGSVGElement>>
    | React.ReactElement<React.SVGProps<SVGSVGElement>>[]
  /**
   * Título do ícone para acessibilidade
   * @example 'Ícone de um avião'
   * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Element/title
   */
  title?: string
  /**
   * ID do título do ícone para acessibilidade
   * @example 'airplane-icon-title'
   */
  titleId?: string
} & ComponentCSSProp &
  React.SVGProps<SVGSVGElement>

const getSize = (size: number | string) =>
  typeof size === 'number' || /^\d+$/.test(size) ? `${size}px` : size

const SvgStyled = styled('svg', {})

export const Icon = React.forwardRef(
  (
    { color = 'currentColor', size = 24, children, title, titleId, ...props }: IconProps,
    ref: React.Ref<SVGSVGElement>,
  ) => {
    const sizeValue = getSize(size)
    const generatedId = useId()
    const finalTitleId = titleId || generatedId
    return (
      <SvgStyled
        {...props}
        ref={ref || createRef()}
        {...(title && {
          'aria-labelledby': finalTitleId,
          role: 'img',
        })}
        aria-hidden={title ? undefined : true}
        css={{
          fill: color,
          width: sizeValue,
          height: sizeValue,
          ...(props.css || {}),
        }}
      >
        {title && <title id={finalTitleId}>{title}</title>}
        {children}
      </SvgStyled>
    )
  },
)
